import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeftRight,
  Landmark,
  Banknote,
  CheckCircle2,
  ScanSearch,
  ShieldAlert,
  Waypoints,
  BrainCircuit,
  Activity,
  Sparkles,
  TrendingUp,
  Clock3,
  AlertTriangle,
  Gauge,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
 * "Ultimate Builder OS" pitch sections for the landing page:
 *   XeroGapSection        — the accountant-vs-builder gap + bank-feed → cost-centre mock
 *   UnfairAdvantagesGrid  — the four "unfair advantages"
 *   SitePulseSection      — Daily Site Pulse (AI insights) teaser
 * All mock-ups are CSS-only with fictional numbers — nothing from a real workspace.
 * ------------------------------------------------------------------------- */

export const PRIMARY_CTA = "Join the Builder Revolution - Get Started";

const Container = ({ className = "", children }) => (
  <div className={cn("mx-auto w-full max-w-[1120px] px-4 sm:px-6 lg:px-8", className)}>{children}</div>
);

const Kicker = ({ children }) => <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{children}</p>;

const FRAME = "rounded-[16px] border border-[rgba(11,42,74,0.14)] bg-white shadow-[var(--shadow-lift)]";

/* ---- Bank feed → cost centres mock ------------------------------------------------------ */
const FEED = [
  { who: "Bunnings — Box Hill", ref: "Spend money · 24 Sept", amt: "-$1,284.60", cc: "3.02 Frame & trusses", job: "42 Cadorna St" },
  { who: "Reece Plumbing", ref: "Bill · 23 Sept", amt: "-$3,940.00", cc: "6.01 Plumbing rough-in", job: "8 Brunswick Rd" },
  { who: "J. Smith Carpentry", ref: "Bill · 22 Sept", amt: "-$6,600.00", cc: "3.02 Frame — labour", job: "42 Cadorna St" },
  { who: "Theo — deposit", ref: "Receive money · 22 Sept", amt: "+$52,273.00", cc: "Progress claim 3", job: "5 Armstrong Rd", income: true },
];

const CENTRES = [
  { code: "3.02", name: "Frame & trusses", spent: 38.2, budget: 48, tone: "ok" },
  { code: "6.01", name: "Plumbing rough-in", spent: 9.4, budget: 22, tone: "ok" },
  { code: "4.01", name: "External cladding", spent: 61.8, budget: 64, tone: "warn" },
  { code: "2.01", name: "Footings & slab", spent: 41.1, budget: 41, tone: "ok" },
];

export const BankFeedMockup = ({ className }) => (
  <div
    className={cn("relative overflow-hidden p-4 sm:p-5", FRAME, className)}
    role="img"
    aria-label="BuiltOS reconciling a live bank feed directly against job cost centres"
    data-testid="landing-bankfeed-mockup"
  >
    <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
      {/* bank feed */}
      <div className="rounded-xl border border-border bg-background/60 p-3">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
            <Landmark className="h-3.5 w-3.5 text-primary" /> <span className="whitespace-nowrap">Live bank feed</span>
          </p>
          <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--chart-received)/0.12)] px-2 py-0.5 text-[9px] font-semibold text-[hsl(var(--chart-received))]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[hsl(var(--chart-received))]" aria-hidden="true" /> synced 2 min ago
          </span>
        </div>
        <ul className="mt-2 divide-y divide-border/70">
          {FEED.map((f) => (
            <li key={f.who} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-[11px] font-medium text-foreground">{f.who}</p>
                <p className="truncate text-[10px] text-muted-foreground">{f.ref}</p>
              </div>
              <p className={cn("shrink-0 font-mono-nums text-[11px] font-semibold", f.income ? "text-[hsl(var(--chart-received))]" : "text-foreground")}>{f.amt}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* connector */}
      <div className="flex items-center justify-center md:flex-col md:gap-2">
        <span className="hidden h-full w-px bg-border md:block" aria-hidden="true" />
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-accent text-primary shadow-[var(--shadow-card)]">
          <ArrowLeftRight className="h-4 w-4 rotate-90 md:rotate-0" />
        </span>
        <span className="hidden h-full w-px bg-border md:block" aria-hidden="true" />
      </div>

      {/* cost centres */}
      <div className="rounded-xl border border-border bg-background/60 p-3">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
            <Banknote className="h-3.5 w-3.5 text-primary" /> <span className="whitespace-nowrap">Job cost centres · 42 Cadorna St</span>
          </p>
          <span className="whitespace-nowrap text-[9px] font-medium text-muted-foreground">budget vs actual</span>
        </div>
        <ul className="mt-2 space-y-2.5">
          {CENTRES.map((c) => {
            const pct = Math.min(100, Math.round((c.spent / c.budget) * 100));
            return (
              <li key={c.code}>
                <div className="flex items-center justify-between text-[10px]">
                  <p className="truncate font-medium text-foreground">
                    <span className="font-mono-nums text-muted-foreground">{c.code}</span> {c.name}
                  </p>
                  <p className="shrink-0 font-mono-nums text-muted-foreground">
                    ${c.spent}k <span className="text-foreground/40">/</span> ${c.budget}k
                  </p>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]">
                  <div
                    className={cn("h-full rounded-full", c.tone === "warn" ? "bg-[hsl(var(--chart-expenses))]" : "bg-primary")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-[hsl(var(--chart-received)/0.3)] bg-[hsl(var(--chart-received)/0.08)] px-2.5 py-1.5">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold text-[hsl(var(--chart-received))]">
            <CheckCircle2 className="h-3.5 w-3.5" /> 4 of 4 lines reconciled to site budget
          </p>
          <span className="font-mono-nums text-[10px] font-semibold text-foreground">Margin 21.4%</span>
        </div>
      </div>
    </div>
  </div>
);

/* ---- Section 1: the Xero gap ------------------------------------------------------------ */
const GAP_POINTS = [
  { icon: Landmark, text: "Every bank line lands against a job and a cost centre — not just a chart-of-accounts code." },
  { icon: Gauge, text: "Budget vs actual moves the moment money moves, so overruns show up in days, not at BAS time." },
  { icon: ArrowLeftRight, text: "Two-way Xero sync keeps your accountant happy while the site view stays builder-first." },
];

export const XeroGapSection = () => (
  <section id="why-builtos" className="py-16 sm:py-20" data-testid="landing-xero-gap">
    <Container>
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Kicker>The accounting gap</Kicker>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-xero-gap-headline">
            Xero was built for accountants. <span className="text-primary">BuiltOS was built for Builders.</span>
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="landing-xero-gap-body">
            A general ledger tells you what you spent. It can't tell you which stage on which site is eating your margin. BuiltOS
            bridges that gap by linking your live bank feeds directly to project cost centres — so every transaction is
            allocated, reconciled and measured against the budget the day it clears.
          </p>
          <blockquote
            className="mt-6 border-l-2 border-primary pl-4 font-heading text-lg font-semibold leading-snug text-foreground sm:text-xl"
            data-testid="landing-xero-gap-hook"
          >
            “Directly reconcile every nail, board, and hour against your site budget in real-time.”
          </blockquote>
          <ul className="mt-6 space-y-3">
            {GAP_POINTS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm leading-6 text-foreground/90">
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <BankFeedMockup />
        </div>
      </div>
    </Container>
  </section>
);

/* ---- Section 2: four unfair advantages -------------------------------------------------- */
export const ADVANTAGES = [
  { key: "ai-vision-takeoff", icon: ScanSearch, title: "AI Vision Takeoff", body: "Drag, drop, and estimate. Plans to BOQ in minutes." },
  { key: "margin-guard-cfo", icon: ShieldAlert, title: "Margin Guard CFO", body: "Automatic alerts when a stage starts to bleed profit." },
  { key: "ripple-effect-scheduler", icon: Waypoints, title: "Ripple-Effect Scheduler", body: "Move one date, notify your entire supply chain instantly." },
  { key: "organic-learning", icon: BrainCircuit, title: "Organic Learning", body: "A system that gets smarter with every invoice you process." },
];

export const UnfairAdvantagesGrid = () => (
  <section id="advantages" className="border-y border-border bg-[hsl(210_28%_96%)] py-16 sm:py-20" data-testid="landing-advantages">
    <Container>
      <div className="max-w-[60ch]">
        <Kicker>Four unfair advantages</Kicker>
        <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl">The parts of the job software never did for you.</h2>
        <p className="mt-3 text-base leading-7 text-muted-foreground">
          Not another job-tracker. These four engines run in the background of every BuiltOS workspace.
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
        {ADVANTAGES.map(({ key, icon: Icon, title, body }, i) => (
          <article
            key={key}
            className="group relative overflow-hidden rounded-[16px] border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-[var(--shadow-lift)]"
            data-testid={`landing-advantage-${key}-card`}
          >
            <span className="absolute right-4 top-4 font-mono-nums text-[11px] font-semibold text-muted-foreground/60">0{i + 1}</span>
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-card)] ring-1 ring-primary/20 transition-transform duration-150 group-hover:scale-105">
              <Icon className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold">{title}</h3>
            <p className="mt-2 text-[15px] leading-6 text-muted-foreground">{body}</p>
          </article>
        ))}
      </div>
    </Container>
  </section>
);

/* ---- Section 3: Daily Site Pulse teaser --------------------------------------------------- */
const PULSE = [
  { icon: TrendingUp, tag: "+$4.1k margin", tone: "good", text: "Cladding on 42 Cadorna is tracking 12% over budget — swap the second fixing crew for the quoted subbie rate before Monday." },
  { icon: Clock3, tag: "saves 2 days", tone: "info", text: "Plaster on 8 Brunswick can start Thursday: frame inspection passed and stock is on site — pull the booking forward." },
  { icon: AlertTriangle, tag: "protect claim 3", tone: "warn", text: "Progress claim 3 on 5 Armstrong is 9 days overdue — send the reminder today to keep September cash flow positive." },
];

const TAG_TONE = {
  good: "bg-[hsl(var(--chart-received)/0.12)] text-[hsl(var(--chart-received))]",
  info: "bg-accent text-primary",
  warn: "bg-[hsl(var(--chart-expenses)/0.12)] text-[hsl(var(--chart-expenses))]",
};

export const SitePulseMockup = ({ className }) => (
  <div className={cn("relative overflow-hidden p-5", FRAME, className)} role="img" aria-label="Daily Site Pulse — AI suggestions for today's sites" data-testid="landing-site-pulse-mockup">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          <Activity className="h-3.5 w-3.5" /> Daily Site Pulse
        </p>
        <p className="mt-1 font-heading text-base font-semibold text-foreground">Thursday · 5 active sites</p>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
        <Sparkles className="h-3 w-3 text-primary" /> generated 6:40 am
      </span>
    </div>

    <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
      <div className="flex items-center justify-between text-[11px]">
        <p className="font-semibold text-foreground">Blended margin</p>
        <p className="font-mono-nums text-muted-foreground">
          target <span className="font-semibold text-foreground">20.0%</span>
        </p>
      </div>
      <div className="mt-2 flex items-end gap-3">
        <p className="font-heading text-3xl font-semibold leading-none text-foreground">21.4%</p>
        <p className="pb-0.5 text-[11px] font-medium text-[hsl(var(--chart-received))]">+1.4 pts above target</p>
      </div>
      <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-foreground/[0.08]">
        <div className="h-full rounded-full bg-primary" style={{ width: "71%" }} />
        <span className="absolute inset-y-0 left-[66%] w-px bg-foreground/50" aria-hidden="true" />
      </div>
    </div>

    <ul className="mt-3 space-y-2">
      {PULSE.map(({ icon: Icon, tag, tone, text }) => (
        <li key={tag} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", TAG_TONE[tone])}>{tag}</span>
            <p className="mt-1 text-[12px] leading-5 text-foreground/90">{text}</p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

export const SitePulseSection = () => (
  <section id="site-pulse" className="border-y border-border bg-[hsl(210_28%_96%)] py-16 sm:py-20" data-testid="landing-site-pulse">
    <Container>
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-6">
          <SitePulseMockup />
        </div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Kicker>AI insights</Kicker>
          <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl" data-testid="landing-site-pulse-headline">
            Your Daily Site Pulse. A CFO and a site manager in your inbox before the first coffee.
          </h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="landing-site-pulse-body">
            Every morning BuiltOS reads your schedule, your bank feed and your claims, then hands you three or four plain-English
            suggestions: where to pull a trade forward, which stage is starting to bleed, and what to chase so the month stays cash
            positive — all tuned to protect your 20% margin.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              "Efficiency moves ranked by dollars saved",
              "Margin alerts before a stage overruns",
              "Overdue claims and bills to chase today",
              "Auto-refreshed daily, re-run any time",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm leading-6 text-foreground/90">
                <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[hsl(var(--chart-received))]" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Button
              asChild
              size="lg"
              className="h-11 rounded-[10px] px-6 text-[15px] shadow-[var(--shadow-card)] transition-[box-shadow,transform,background-color] duration-150 hover:-translate-y-px hover:shadow-[var(--shadow-lift)] active:translate-y-0 active:scale-[0.98]"
              data-testid="landing-site-pulse-cta"
            >
              <Link to="/signup">
                {PRIMARY_CTA} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Container>
  </section>
);
